/**
 * assessmentSections — the gap interview's section anchoring helpers
 * (2026-07 drafter redesign).
 *
 * A follow-up flag names its section in free text (the model writes
 * "Medical Assessment", "Header", "ADL/IADL", ...). These helpers
 * normalize that free text onto the report's canonical section keys so:
 *   - the draft can mark sections that carry open gaps (count chips on
 *     the headings),
 *   - each flag can jump-link to its section (anchor ids), and
 *   - the section outline can carry per-section gap counts.
 *
 * Unmatched section strings are a real, tolerated case (the model may
 * coin a label); they simply stay roll-up-only (no anchor, no chip).
 */

import type { AdlIadlEntry, AssessmentFollowUpQuestion, AssessmentReportSections } from './api';

export type AssessmentSectionKey = keyof AssessmentReportSections;

/** DOM anchor id for a section heading (the header block gets its own). */
export function sectionAnchorId(key: AssessmentSectionKey | 'header'): string {
  return `assessment-section-${key}`;
}

function normalize(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * Alias table: normalized flag-section text -> canonical section key (or
 * 'header' for the DATE/TO/FROM/RE block). Sub-section names resolve to
 * their parent section.
 */
const SECTION_ALIASES: ReadonlyArray<[string, AssessmentSectionKey | 'header']> = [
  ['header', 'header'],
  ['report header', 'header'],
  ['history', 'history'],
  ['medical assessment', 'medical_assessment'],
  ['medical', 'medical_assessment'],
  ['medications', 'medical_assessment'],
  ['current medications', 'medical_assessment'],
  ['providers', 'medical_assessment'],
  ['home care status', 'medical_assessment'],
  ['past surgical history', 'medical_assessment'],
  ['recent hospitalization history', 'medical_assessment'],
  ['insurance', 'medical_assessment'],
  ['pharmacy', 'medical_assessment'],
  ['neurological', 'neurological'],
  ['presentation', 'neurological'],
  ['memory cognition', 'neurological'],
  ['mood and behavior', 'neurological'],
  ['mood behavior', 'neurological'],
  ['neuro medical', 'neurological'],
  ['sensory', 'sensory'],
  ['sleep', 'sleep'],
  ['pain', 'pain'],
  ['cardiovascular', 'cardiovascular'],
  ['respiratory', 'respiratory'],
  ['gastroenterological', 'gastroenterological'],
  ['urinary', 'urinary'],
  ['genitourinary', 'urinary'],
  ['gu', 'urinary'],
  ['bladder', 'urinary'],
  ['urinary bladder', 'urinary'],
  ['musculoskeletal', 'musculoskeletal'],
  ['integumentary', 'integumentary'],
  ['endocrine', 'endocrine'],
  ['functional assessment', 'functional_assessment'],
  ['adl iadl', 'functional_assessment'],
  ['adl iadl table', 'functional_assessment'],
  ['adl iadl functional assessment', 'functional_assessment'],
  ['social assessment', 'social_assessment'],
  ['home safety assessment', 'home_safety_assessment'],
  ['home safety', 'home_safety_assessment'],
  ['summary and recommendations', 'summary_and_recommendations'],
  ['summary', 'summary_and_recommendations'],
  ['recommendations', 'summary_and_recommendations'],
];

const ALIAS_MAP = new Map<string, AssessmentSectionKey | 'header'>(SECTION_ALIASES);

/**
 * Resolve a flag's free-text section label onto a canonical section key
 * (or 'header'). null = no anchor (the flag stays roll-up-only).
 */
export function sectionKeyForFlag(sectionLabel: string): AssessmentSectionKey | 'header' | null {
  return ALIAS_MAP.get(normalize(sectionLabel)) ?? null;
}

/**
 * Per-section open-gap counts for the draft's heading markers and the
 * section outline. Keys are canonical section keys plus 'header'.
 */
export function gapCountsBySection(
  questions: readonly AssessmentFollowUpQuestion[],
): Partial<Record<AssessmentSectionKey | 'header', number>> {
  const counts: Partial<Record<AssessmentSectionKey | 'header', number>> = {};
  for (const q of questions) {
    const key = sectionKeyForFlag(q.section);
    if (key === null) continue;
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}

// ---------------------------------------------------------------------
// Paragraph trust states: leaf enumeration + change diffing. The page
// diffs the pre-run report against the run's result to mark
// changed-by-last-run paragraphs and to write the one-line run summary
// ("Updated Medical Assessment and Pain. Your edits elsewhere
// untouched.") — the PROOF behind the merge promise.
// ---------------------------------------------------------------------

/** Display titles (Title Case) for the run summary + outline. */
export const SECTION_DISPLAY_TITLES: Record<AssessmentSectionKey | 'header', string> = {
  header: 'Header',
  history: 'History',
  medical_assessment: 'Medical Assessment',
  neurological: 'Neurological',
  sensory: 'Sensory',
  sleep: 'Sleep',
  pain: 'Pain',
  cardiovascular: 'Cardiovascular',
  respiratory: 'Respiratory',
  gastroenterological: 'Gastroenterological',
  urinary: 'Urinary',
  musculoskeletal: 'Musculoskeletal',
  integumentary: 'Integumentary',
  endocrine: 'Endocrine',
  functional_assessment: 'Functional Assessment',
  social_assessment: 'Social Assessment',
  home_safety_assessment: 'Home Safety Assessment',
  summary_and_recommendations: 'Summary and Recommendations',
};

/**
 * Serialize the meds/providers table rows (prompt v8) to one stable
 * string per table, so diffLeafPaths flags a run that touched a table and
 * the run summary names Medical Assessment. Absent and [] serialize
 * identically ('') — a server round-trip that materializes empty arrays
 * on a legacy draft must never read as a change.
 */
function serializeTableRows(rows: ReadonlyArray<object> | undefined): string {
  return (rows ?? []).map((r) => Object.values(r).join('|')).join('\n');
}

/** Flatten a report's prose leaves onto their paragraph-state paths. */
export function enumerateLeafValues(sections: AssessmentReportSections): Record<string, string> {
  const leaves: Record<string, string> = {
    history: sections.history,
    'medical_assessment.overview': sections.medical_assessment.overview,
    'medical_assessment.medications': serializeTableRows(sections.medical_assessment.medications),
    'medical_assessment.providers': serializeTableRows(sections.medical_assessment.providers),
    'medical_assessment.home_care_status': sections.medical_assessment.home_care_status,
    'medical_assessment.past_surgical_history': sections.medical_assessment.past_surgical_history,
    'medical_assessment.recent_hospitalization_history':
      sections.medical_assessment.recent_hospitalization_history,
    'medical_assessment.insurance': sections.medical_assessment.insurance ?? '',
    'medical_assessment.pharmacy': sections.medical_assessment.pharmacy ?? '',
    'neurological.presentation': sections.neurological.presentation,
    'neurological.memory_cognition': sections.neurological.memory_cognition,
    'neurological.mood_behavior': sections.neurological.mood_behavior,
    'neurological.neuro_medical': sections.neurological.neuro_medical,
    sensory: sections.sensory,
    sleep: sections.sleep,
    pain: sections.pain,
    cardiovascular: sections.cardiovascular,
    respiratory: sections.respiratory,
    gastroenterological: sections.gastroenterological,
    urinary: sections.urinary ?? '',
    musculoskeletal: sections.musculoskeletal,
    integumentary: sections.integumentary,
    endocrine: sections.endocrine,
    functional_assessment: sections.functional_assessment,
    social_assessment: sections.social_assessment,
    home_safety_assessment: sections.home_safety_assessment,
    'summary_and_recommendations.synthesis': sections.summary_and_recommendations.synthesis,
  };
  sections.summary_and_recommendations.recommendations.forEach((rec, i) => {
    leaves[`summary_and_recommendations.recommendations.${i}`] = `${rec.label}. ${rec.body}`;
  });
  return leaves;
}

/** Leaf paths whose text a run changed (added/removed leaves count too). */
export function diffLeafPaths(
  prev: AssessmentReportSections,
  next: AssessmentReportSections,
): string[] {
  const a = enumerateLeafValues(prev);
  const b = enumerateLeafValues(next);
  const paths = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...paths].filter((p) => (a[p] ?? '') !== (b[p] ?? ''));
}

/** Top-level section title for a leaf path ('medical_assessment.overview' -> 'Medical Assessment'). */
export function sectionTitleForPath(path: string): string {
  // ADL/IADL rows live under the Functional Assessment section.
  if (path.startsWith('adl_iadl.')) return 'Functional Assessment';
  const top = path.split('.')[0] as AssessmentSectionKey;
  return SECTION_DISPLAY_TITLES[top] ?? top;
}

/**
 * ADL/IADL rows whose level or notes a run changed, as trust-state paths
 * ('adl_iadl.<activity>'). Keyed by activity (not index) because the
 * validator is order-independent, so a run may reorder rows without
 * changing them. Companion to diffLeafPaths (drafter founder polish,
 * 2026-07-06: the table is editable, so its rows carry trust states too).
 */
export function diffAdlPaths(
  prev: readonly AdlIadlEntry[],
  next: readonly AdlIadlEntry[],
): string[] {
  const byActivity = new Map(prev.map((e) => [e.activity, e]));
  const changed: string[] = [];
  for (const e of next) {
    const p = byActivity.get(e.activity);
    if (!p || p.level !== e.level || p.notes !== e.notes) {
      changed.push(`adl_iadl.${e.activity}`);
    }
  }
  return changed;
}

// ---------------------------------------------------------------------
// Medical Assessment overview blocks (drafter founder polish, 2026-07-06,
// item 1): the "Current medications:" / "Current providers:" lead-in
// lines render as REAL subheadings (the "Home Care Status" treatment).
// LEGACY-DRAFT FALLBACK as of prompt v8 (meds/providers tables): new
// drafts carry the lists as structured medications/providers arrays and
// their overview has no label lines (this splitter passes it through as
// one prose block); v7-and-earlier saved drafts keep rendering their
// in-overview lists through it unchanged. MIRROR of
// api/_shared/aiTools/medicalOverviewBlocks.ts (the .docx side) — keep
// the regex + canonicalization in lockstep; both sides carry this pointer.
// ---------------------------------------------------------------------

/** A standalone label line introducing one of the two bulleted lists. */
export const MEDICAL_OVERVIEW_LABEL_RE =
  /^\s*current\s+(medications?|meds|(?:medical\s+)?providers?|physicians?)\s*:?\s*$/i;

/** One rendered block: exactly one of heading/text is non-empty. */
export interface MedicalOverviewBlock {
  heading: string;
  text: string;
}

/**
 * Split the overview prose into ordered heading/text blocks. No label
 * lines -> one text block carrying the overview verbatim. Blank lines
 * adjacent to a heading are absorbed; interior newlines within a prose
 * block are preserved (whitespace-pre-wrap keeps the "- " bullet lines).
 */
export function splitMedicalOverviewBlocks(overview: string): MedicalOverviewBlock[] {
  const blocks: MedicalOverviewBlock[] = [];
  let buffer: string[] = [];

  const flush = (): void => {
    while (buffer.length > 0 && buffer[0]!.trim().length === 0) buffer.shift();
    while (buffer.length > 0 && buffer[buffer.length - 1]!.trim().length === 0) buffer.pop();
    if (buffer.length > 0) blocks.push({ heading: '', text: buffer.join('\n') });
    buffer = [];
  };

  for (const line of overview.split('\n')) {
    const m = MEDICAL_OVERVIEW_LABEL_RE.exec(line);
    if (m) {
      flush();
      // "medical providers" also starts with "med"; key on the provider nouns.
      blocks.push({
        heading: /provider|physician/i.test(m[1]!) ? 'Current Providers' : 'Current Medications',
        text: '',
      });
    } else {
      buffer.push(line);
    }
  }
  flush();

  return blocks;
}

/**
 * The one-line run summary. Empty changed set -> a calm no-change line;
 * otherwise "Updated X and Y. Your edits elsewhere untouched."
 */
export function runChangeSummary(changedPaths: readonly string[]): string {
  if (changedPaths.length === 0) {
    return 'Nothing changed in the report sections. Your edits are untouched.';
  }
  const titles = [...new Set(changedPaths.map(sectionTitleForPath))];
  const list =
    titles.length === 1
      ? titles[0]!
      : titles.length === 2
        ? `${titles[0]} and ${titles[1]}`
        : `${titles.slice(0, -1).join(', ')}, and ${titles[titles.length - 1]}`;
  return `Updated ${list}. Your edits elsewhere untouched.`;
}
